import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, HardHat, Edit, Copy, RefreshCw, Ban, RotateCcw, CornerDownRight } from 'lucide-react';
import { toast } from 'sonner';
import WhatsAppDugmesi from '@/components/WhatsAppDugmesi';
import WhatsAppGonder from '@/components/WhatsAppGonder';
import IsKalemleriDialog from '@/components/insaat/IsKalemleriDialog';

const BOS_YETKILI = { ad: '', telefon: '', eposta: '' };

// Modül seviyesinde: sayfa içinde tanımlansaydı her tuş vuruşunda yeni bir
// bileşen tipi olur, kutucuk odağını kaybederdi.
function YetkiliAlanlari({ baslik, deger, onChange, testId }) {
  const set = (alan) => (e) => onChange({ ...deger, [alan]: e.target.value });
  return (
    <div className="space-y-2 border border-[#e8dfd0] rounded-lg p-3 bg-white/50" data-testid={testId}>
      <p className="text-sm font-semibold text-[#3F3E39]">{baslik}</p>
      <Input value={deger.ad} onChange={set('ad')} placeholder="Ad soyad" className="bg-white border-[#d0c4b0]" />
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
        <Input value={deger.telefon} onChange={set('telefon')} placeholder="Telefon" type="tel" className="bg-white border-[#d0c4b0]" />
        <Input value={deger.eposta} onChange={set('eposta')} placeholder="E-posta" type="email" className="bg-white border-[#d0c4b0]" />
      </div>
    </div>
  );
}

const yetkiliHucresi = (y) => {
  if (!y || (!y.ad && !y.telefon && !y.eposta)) return <span className="text-[#c9bfae]">—</span>;
  return (
    <div className="text-xs leading-tight space-y-0.5">
      {y.ad && <p className="font-medium text-[#1a1916]">{y.ad}</p>}
      {y.telefon && (
        <p className="flex items-center gap-1 text-[#3F3E39]">{y.telefon} <WhatsAppDugmesi telefon={y.telefon} /></p>
      )}
      {y.eposta && <a href={`mailto:${y.eposta}`} className="text-[#2e6fa0] hover:underline break-all">{y.eposta}</a>}
    </div>
  );
};

export default function SubcontractorList() {
  const [subs, setSubs] = useState([]);
  const [sites, setSites] = useState([]);
  // {id?, parent?, name, firma, santiye, default_site_id}
  const [form, setForm] = useState(null);
  const [loading, setLoading] = useState(false);
  const [confirmRegen, setConfirmRegen] = useState(null);
  const [kalemSub, setKalemSub] = useState(null);   // iş kalemleri penceresi

  useEffect(() => { fetchSubs(); fetchSites(); }, []);

  const fetchSubs = async () => {
    try {
      const res = await api.get('/insaat/subcontractors');
      setSubs(res.data);
    } catch (err) { console.error(err); }
  };

  const fetchSites = async () => {
    try {
      const res = await api.get('/insaat/sites');
      setSites(res.data);
    } catch (err) { console.error(err); }
  };

  const anaTaseronlar = subs.filter((s) => !s.parent_id);
  const altlari = (id) => subs.filter((s) => s.parent_id === id);

  const openNew = (parent = null) => setForm({
    parent, name: '', firma: { ...BOS_YETKILI }, santiye: { ...BOS_YETKILI },
    default_site_id: parent?.default_site_id || '',
  });
  const openEdit = (sub) => setForm({
    id: sub.id, parent: sub.parent_id ? subs.find((s) => s.id === sub.parent_id) : null,
    name: sub.name,
    firma: { ...BOS_YETKILI, ...(sub.firma_yetkilisi || {}) },
    santiye: { ...BOS_YETKILI, ...(sub.santiye_yetkilisi || {}) },
    default_site_id: sub.default_site_id || '',
  });

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setLoading(true);
    try {
      const payload = {
        name: form.name, santiye_yetkilisi: form.santiye,
        default_site_id: form.default_site_id || null,
        // Alt taşeronun firma yetkilisi tutulmuyor (sunucu da yok sayıyor)
        ...(form.parent ? {} : { firma_yetkilisi: form.firma }),
      };
      if (form.id) {
        await api.patch(`/insaat/subcontractors/${form.id}`, payload);
        toast.success('Alt yüklenici güncellendi');
      } else {
        await api.post('/insaat/subcontractors', { ...payload, parent_id: form.parent?.id || null });
        toast.success(form.parent ? 'Alt taşeron eklendi' : 'Alt yüklenici oluşturuldu');
      }
      setForm(null);
      fetchSubs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    }
    setLoading(false);
  };

  const linkFor = (sub) => `${window.location.origin}/rapor/${sub.report_token}`;

  const copyLink = async (sub) => {
    try {
      await navigator.clipboard.writeText(linkFor(sub));
      toast.success('Bağlantı kopyalandı');
    } catch (err) {
      toast.error('Kopyalanamadı — bağlantıyı elle seçip kopyalayın');
    }
  };

  const doRegenerate = async (sub) => {
    try {
      const res = await api.post(`/insaat/subcontractors/${sub.id}/regenerate-token`);
      setSubs((prev) => prev.map((s) => (s.id === sub.id ? res.data : s)));
      toast.success('Yeni bağlantı oluşturuldu — eski bağlantı artık geçersiz');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    }
    setConfirmRegen(null);
  };

  const toggleRevoke = async (sub) => {
    try {
      const uc = sub.token_revoked ? 'restore-token' : 'revoke-token';
      const res = await api.post(`/insaat/subcontractors/${sub.id}/${uc}`);
      setSubs((prev) => prev.map((s) => (s.id === sub.id ? res.data : s)));
      toast.success(sub.token_revoked ? 'Bağlantı tekrar aktif edildi' : 'Bağlantı devre dışı bırakıldı');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    }
  };

  const toggleActive = async (sub) => {
    const altSayisi = altlari(sub.id).length;
    if (sub.is_active && altSayisi > 0 && !window.confirm(
      `${sub.name} pasife alınınca ${altSayisi} alt taşeronunun bağlantısı da kapanacak. Devam edilsin mi?`)) return;
    try {
      await api.patch(`/insaat/subcontractors/${sub.id}`, { is_active: !sub.is_active });
      fetchSubs();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Hata oluştu');
    }
  };

  const satir = (s, alt) => {
    const siteName = sites.find((st) => st.id === s.default_site_id)?.name || '-';
    const ust = alt ? subs.find((x) => x.id === s.parent_id) : null;
    const linkKapali = s.token_revoked || !s.is_active || (ust && !ust.is_active);
    return (
      <TableRow key={s.id} className={`erp-table-row border-[#d0c4b0] ${alt ? 'bg-[#faf7f2]' : ''}`}
        data-testid={`subcontractor-row-${s.id}`}>
        <TableCell className="font-medium text-[#1a1916]">
          <div className={`flex items-center gap-2 ${alt ? 'pl-6' : ''}`}>
            {alt ? <CornerDownRight size={14} className="text-[#8a7e70]" /> : <HardHat size={14} className="text-[#8a7e70]" />}
            <span>{s.name}</span>
          </div>
          {alt && <p className="text-[10px] text-[#8a7e70] pl-12">alt taşeron</p>}
        </TableCell>
        <TableCell>{alt ? <span className="text-[#c9bfae]">—</span> : yetkiliHucresi(s.firma_yetkilisi)}</TableCell>
        <TableCell>{yetkiliHucresi(s.santiye_yetkilisi)}</TableCell>
        <TableCell className="text-sm text-[#8a7e70]">
          <div>{siteName}</div>
          {s.depo_id && (
            <Link to={`/erp/warehouses/${s.depo_id}`} className="text-xs text-[#2e6fa0] hover:underline"
              data-testid={`depo-link-${s.id}`}>
              Alt yüklenici deposu
            </Link>
          )}
        </TableCell>
        <TableCell>
          <div className="flex items-center gap-1">
            {linkKapali ? (
              <span className="status-badge bg-[#7a3535]/10 text-[#7a3535]"
                title={s.token_revoked ? 'Bağlantı iptal edildi' : 'Alt yüklenici ya da bağlı olduğu alt yüklenici pasif'}>
                Kapalı
              </span>
            ) : (
              <span className="text-xs font-mono text-[#8a7e70] truncate max-w-[120px]">{linkFor(s)}</span>
            )}
            <Button variant="ghost" size="icon" onClick={() => copyLink(s)} className="h-7 w-7" title="Kopyala" data-testid={`copy-link-${s.id}`}>
              <Copy size={13} />
            </Button>
            <WhatsAppGonder modul="insaat_rapor" kayitId={s.id} etiket="" variant="ghost"
              disabled={linkKapali} className="h-7 w-7 p-0" testId={`wa-link-${s.id}`} />
            <Button variant="ghost" size="icon" onClick={() => setConfirmRegen(s)} className="h-7 w-7" title="Yeniden Oluştur" data-testid={`regen-link-${s.id}`}>
              <RefreshCw size={13} />
            </Button>
            <Button variant="ghost" size="icon" onClick={() => toggleRevoke(s)} className="h-7 w-7"
              title={s.token_revoked ? 'Tekrar Aktif Et' : 'İptal Et'} data-testid={`revoke-link-${s.id}`}>
              {s.token_revoked ? <RotateCcw size={13} /> : <Ban size={13} />}
            </Button>
          </div>
        </TableCell>
        <TableCell>
          <button onClick={() => toggleActive(s)} data-testid={`toggle-active-${s.id}`}
            className={`status-badge ${s.is_active ? 'bg-[#3d6e4e]/10 text-[#3d6e4e]' : 'bg-[#e8dfd0] text-[#8a7e70]'}`}>
            {s.is_active ? 'Aktif' : 'Pasif'}
          </button>
        </TableCell>
        <TableCell className="text-right whitespace-nowrap">
          {!alt && (
            <Button variant="outline" size="sm" onClick={() => setKalemSub(s)} data-testid={`is-kalemleri-${s.id}`}
              className={`h-8 px-2 text-xs mr-1 ${s.kalem_sayisi ? 'border-[#d0c4b0]' : 'border-[#9b3b2f]/40 text-[#9b3b2f]'}`}
              title="Yapacağı işler (pozlar) ve anlaşılan birim fiyatlar">
              İş Kalemleri ({s.kalem_sayisi || 0})
            </Button>
          )}
          {!alt && (
            <Button variant="ghost" size="sm" onClick={() => openNew(s)} className="h-8 px-2 text-xs" data-testid={`add-alt-${s.id}`}>
              <Plus size={12} className="mr-0.5" /> Alt Taşeron
            </Button>
          )}
          <Button variant="ghost" size="icon" onClick={() => openEdit(s)} className="h-8 w-8" data-testid={`edit-subcontractor-${s.id}`}>
            <Edit size={14} />
          </Button>
        </TableCell>
      </TableRow>
    );
  };

  return (
    <div data-testid="insaat-subcontractor-list-page">
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-[#1a1916] tracking-tight">Alt Yükleniciler</h1>
        <Button onClick={() => openNew()} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916] font-semibold" data-testid="new-subcontractor-btn">
          <Plus size={16} className="mr-2" /> Yeni Alt Yüklenici
        </Button>
      </div>

      <p className="text-sm text-[#8a7e70] mb-4">
        Her alt yükleniciye ve alt taşerona kalıcı bir günlük rapor bağlantısı verilir; giriş yapmadan bu
        bağlantıdan tutanak ve puantaj girilir. Alt yüklenici kendi raporunda satırı alt taşeronu adına da
        girebilir. Bağlantıyı ele geçiren biri o alt yüklenici adına rapor girebilir — şüpheli durumda
        "Yeniden Oluştur" ile eski bağlantıyı anında geçersiz kılın.
      </p>

      <div className="bg-[#faf7f2] border border-[#d0c4b0] rounded-lg overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow className="bg-[#e8dfd0] hover:bg-[#e8dfd0]">
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Ad</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Firma Yetkilisi</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Şantiye Yetkilisi</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Şantiye / Depo</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Bağlantı</TableHead>
              <TableHead className="text-[#1a1916] font-bold text-xs uppercase">Durum</TableHead>
              <TableHead className="text-right"></TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {anaTaseronlar.flatMap((s) => [satir(s, false), ...altlari(s.id).map((a) => satir(a, true))])}
            {subs.length === 0 && (
              <TableRow><TableCell colSpan={7} className="text-center py-8 text-[#8a7e70]">Alt yüklenici bulunamadı</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={!!form} onOpenChange={(v) => !v && setForm(null)}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0] max-w-lg max-h-[90vh] overflow-y-auto">
          {form && (
            <>
              <DialogHeader>
                <DialogTitle className="text-[#1a1916] font-bold">
                  {form.id ? 'Düzenle' : form.parent ? 'Yeni Alt Taşeron' : 'Yeni Alt Yüklenici'}
                </DialogTitle>
                <DialogDescription className="text-[#8a7e70] text-sm">
                  {form.parent ? `${form.parent.name} alt yüklenicisinin alt taşeronu. Alt taşeronda yalnız şantiye yetkilisi tutulur.`
                    : 'Yetkili bilgileri opsiyoneldir; sonradan tamamlanabilir.'}
                </DialogDescription>
              </DialogHeader>
              <div className="space-y-4">
                <div className="space-y-2">
                  <Label className="text-[#3F3E39]">{form.parent ? 'Alt Taşeron Adı *' : 'Alt Yüklenici Adı *'}</Label>
                  <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                    className="bg-white border-[#d0c4b0]" data-testid="subcontractor-name-input" />
                </div>
                {!form.parent && (
                  <YetkiliAlanlari baslik="Firma Yetkilisi" deger={form.firma} testId="firma-yetkilisi"
                    onChange={(v) => setForm((f) => ({ ...f, firma: v }))} />
                )}
                <YetkiliAlanlari baslik="Şantiye Yetkilisi" deger={form.santiye} testId="santiye-yetkilisi"
                  onChange={(v) => setForm((f) => ({ ...f, santiye: v }))} />
                <div className="space-y-2">
                  <Label className="text-[#3F3E39]">Varsayılan Şantiye</Label>
                  <Select value={form.default_site_id} onValueChange={(v) => setForm((f) => ({ ...f, default_site_id: v }))}>
                    <SelectTrigger className="bg-white border-[#d0c4b0]">
                      <SelectValue placeholder="Seçin (opsiyonel)" />
                    </SelectTrigger>
                    <SelectContent>
                      {sites.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter className="mt-4">
                <Button variant="outline" onClick={() => setForm(null)} className="border-[#d0c4b0]">İptal</Button>
                <Button onClick={handleSave} disabled={loading || !form.name.trim()} className="bg-[#2e2d29] text-[#f5f0e8] hover:bg-[#1a1916]" data-testid="save-subcontractor-btn">
                  {loading ? 'Kaydediliyor...' : 'Kaydet'}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!confirmRegen} onOpenChange={(open) => !open && setConfirmRegen(null)}>
        <DialogContent className="bg-[#faf7f2] border-[#d0c4b0]">
          <DialogHeader>
            <DialogTitle className="text-[#1a1916] font-bold">Bağlantıyı Yeniden Oluştur</DialogTitle>
            <DialogDescription className="text-[#3F3E39] text-sm">
              Eski bağlantı anında geçersiz olacak. Alt yükleniciye yeni bağlantıyı iletmeniz gerekir. Devam edilsin mi?
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmRegen(null)} className="border-[#d0c4b0]">İptal</Button>
            <Button onClick={() => doRegenerate(confirmRegen)} className="bg-[#7a3535] text-white hover:bg-[#5a2525]">
              Yeniden Oluştur
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
      <IsKalemleriDialog sub={kalemSub} open={!!kalemSub} onOpenChange={(v) => !v && setKalemSub(null)}
        onDegisti={fetchSubs} />
    </div>
  );
}
